import { faChevronDown, faChevronUp } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DateDisplay, TruncatedContentDisplay, useElementSize, ShowMoreButton } from '@lib/common';
import { MaintenanceTicketModel } from '@lib/state/api/generated/properties';
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Box,
  Collapse,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import React, { useCallback, useState, Fragment } from 'react';

interface Props {
  tickets: Array<MaintenanceTicketModel>;
  loadMore: () => void;
  isDone: boolean;
}

export function MaintenanceTicketDuplicateDisplay({ tickets, loadMore, isDone }: Props) {
  const [expandedRows, setExpandedRows] = useState<Array<number>>([]);
  const [cellSize, setCellRef] = useElementSize();
  const [alertSize, setAlertRef] = useElementSize();

  const rowExpanded = useCallback((index: number) => expandedRows.includes(index), [expandedRows]);

  const setExpanded = useCallback(
    (index: number) =>
      expandedRows.includes(index)
        ? setExpandedRows(expandedRows.filter(r => r !== index))
        : setExpandedRows([...expandedRows, index]),
    [expandedRows, setExpandedRows]
  );

  return (
    <Alert ref={setAlertRef} severity="warning" style={{ width: '100%' }}>
      <AlertTitle>Possible Duplicates</AlertTitle>
      <TableContainer style={{ alignItems: 'center', alignContent: 'center' }}>
        <Table size="small" style={{ minWidth: alertSize.width * 0.95 }}>
          <TableHead>
            <TableRow>
              <TableCell></TableCell>
              <TableCell>Ticket</TableCell>
              <TableCell>Created Date</TableCell>
              <TableCell>Room Number / Area</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Notes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tickets?.map((ticket, index) => (
              <Fragment key={ticket.ticketNumber}>
                <TableRow>
                  <TableCell>
                    <Tooltip title="Click to see all notes">
                      <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={() => setExpanded(index)}
                      >
                        {rowExpanded(index) ? (
                          <FontAwesomeIcon icon={faChevronUp.iconName} />
                        ) : (
                          <FontAwesomeIcon icon={faChevronDown.iconName} />
                        )}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Typography>{ticket.ticketNumber}</Typography>
                  </TableCell>
                  <TableCell>
                    <DateDisplay date={new Date(ticket.timestamp)} dateFormat="MM/dd/yyyy" />
                  </TableCell>
                  <TableCell>
                    <Typography>
                      {ticket.roomNumber} {ticket.roomNumber && ticket.area && ' / '} {ticket.area}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>{ticket.status}</Typography>
                  </TableCell>
                  <TableCell ref={setCellRef} style={{ width: '55%' }}>
                    <TruncatedContentDisplay width={cellSize.width}>
                      {ticket.description?.[0]}
                    </TruncatedContentDisplay>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                    <Collapse in={rowExpanded(index)} timeout="auto" unmountOnExit>
                      <Box sx={{ margin: 1 }}>
                        <Typography variant="h6" gutterBottom component="div">
                          All Notes
                        </Typography>
                        <ul>
                          {ticket.description?.map((d, i) => (
                            <li key={ticket.id + i}>{d}</li>
                          ))}
                        </ul>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </Fragment>
            ))}
          </TableBody>
        </Table>
        <ShowMoreButton isDone={isDone} loadMore={loadMore} />
      </TableContainer>
    </Alert>
  );
}
